import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { Router } from '@angular/router';
import { NotificationSearchModel, NrSearchModel, SignalRNotificationModel } from 'app/core/models';
import { COMMON_SERVICE, CommonInterface } from 'app/core/services/common.interface';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

@Component({
    selector: 'app-notification-panel',
    templateUrl: './notification-panel.component.html',
    styleUrls: ['./notification-panel.component.css'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush // notifications only change via our own methods now, safe to opt in
})
export class NotificationPanelComponent implements OnInit, OnChanges, OnDestroy {

    // Cached formatter — creating Intl.DateTimeFormat is expensive; reuse one instance
    // for every notification, every render, instead of `new`-ing it per call.
    private static readonly _dateFormatter = new Intl.DateTimeFormat('en-IN', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit'
    });

    //#region Fields & Properties
    nrSearchModel: NrSearchModel = new NrSearchModel();
    @Input() open = false;
    @Output() closePanel = new EventEmitter<void>();

    notifications: SignalRNotificationModel[] = [];
    unreadCount = 0;
    activeTab: 'all' | 'read' | 'readAll' | 'unread' = 'all';
    isLoading = false;

    // ── Cached derived state (recomputed only on data/tab change, NOT every CD cycle) ──
    readCount = 0;
    filteredNotifications: SignalRNotificationModel[] = [];
    todayNotifications: SignalRNotificationModel[] = [];
    olderNotifications: SignalRNotificationModel[] = [];

    private readonly destroy$ = new Subject<void>();
    private readonly _search$ = new Subject<string>();
    searchModel: NotificationSearchModel = new NotificationSearchModel();
    expandedIds = new Set<number>();
    //#endregion

    //#region Constructor
    constructor(
        private _router: Router,
        @Inject(COMMON_SERVICE) private _commonService: CommonInterface,
        private _cdr: ChangeDetectorRef,
    ) { }
    //#endregion

    //#region Methods
    ngOnInit(): void {
        // Debounce search input — was firing one API call per keystroke before.
        this._search$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(value => {
                this.searchModel.searchText = value;
                this.searchModel.page = 1;
                this._loadNotifications(false);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['open'] && this.open) {
            this.searchModel = new NotificationSearchModel();
            this.searchModel.page = 1;
            this._loadNotifications(false);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    close(): void { this.closePanel.emit(); }

    clearAll(): void {
        this.notifications = [];
        this.unreadCount = 0;
        this._recomputeDerivedState();
    }

    private _loadNotifications(mergeWithCurrent: boolean): void {
        this.isLoading = true;
        this._commonService.getNotificationList(this.searchModel)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.isLoading = false;
                    if (res?.status !== 200 || !res.data) { this._cdr.markForCheck(); return; }

                    const incoming: SignalRNotificationModel[] = (res.data || []).map((n: SignalRNotificationModel) => ({
                        ...n,
                        isRead: n.isRead ?? false,
                        timestamp: n.createdOn ? new Date(n.createdOn) : (n.validFrom ? new Date(n.validFrom) : new Date())
                    }));

                    this.notifications = mergeWithCurrent
                        ? this._mergeNotifications(this.notifications, incoming)
                        : incoming;

                    this._updateUnreadCount();
                    this._recomputeDerivedState();
                },
                error: (err) => {
                    this.isLoading = false;
                    console.error('[NotificationPanel] Load failed:', err);
                    this._cdr.markForCheck();
                }
            });
    }

    private _mergeNotifications(current: SignalRNotificationModel[], incoming: SignalRNotificationModel[]): SignalRNotificationModel[] {
        const mergedById = new Map<number, SignalRNotificationModel>();
        [...current, ...incoming].forEach(n => mergedById.set(n.id, n));
        return Array.from(mergedById.values());
    }

    private _updateUnreadCount(): void {
        this.unreadCount = this.notifications.filter(n => !n.isRead).length;
    }

    /**
     * Single-pass recompute of everything the template needs.
     * Called explicitly whenever notifications[] or activeTab actually changes —
     * NOT on every change-detection cycle (that was the CPU cost before).
     */
    private _recomputeDerivedState(): void {
        this.readCount = 0;
        this.filteredNotifications = [];
        this.todayNotifications = [];
        this.olderNotifications = [];

        const todayStr = new Date().toDateString(); // compute once, not per-notification

        for (const n of this.notifications) {
            if (n.isRead) this.readCount++;

            let matchesTab: boolean;
            switch (this.activeTab) {
                case 'read': matchesTab = n.isRead; break;
                case 'unread': matchesTab = !n.isRead; break;
                case 'readAll':
                case 'all':
                default: matchesTab = true; break;
            }
            if (!matchesTab) continue;

            this.filteredNotifications.push(n);

            const ts = n.timestamp ? new Date(n.timestamp) : null;
            const isToday = !!ts && !Number.isNaN(ts.getTime()) && ts.toDateString() === todayStr;
            (isToday ? this.todayNotifications : this.olderNotifications).push(n);
        }

        this._cdr.markForCheck();
    }

    setTab(tab: 'all' | 'read' | 'readAll' | 'unread'): void {
        if (tab === 'readAll') {
            this.nrSearchModel = new NrSearchModel();
            this.nrSearchModel.isReadAll = true;

            this._commonService.notificationMarkAsRead(this.nrSearchModel)
                .subscribe({
                    next: () => {
                        for (const n of this.notifications) n.isRead = true;
                        this.unreadCount = 0;
                        this.activeTab = 'all';
                        this._recomputeDerivedState();
                    },
                    error: (err) => console.error('[NotificationPanel] MarkAllAsRead failed:', err)
                });
            return;
        }

        this.activeTab = tab;
        this._recomputeDerivedState();
    }

    markAsRead(notification: SignalRNotificationModel, event: Event): void {
        event.stopPropagation();

        if (!notification.isRead) {
            this.nrSearchModel = new NrSearchModel();
            this.nrSearchModel.notificationId = notification.id;

            this._commonService.notificationMarkAsRead(this.nrSearchModel)
                .subscribe({
                    next: () => {
                        // mutate in place instead of mapping the whole array — cheaper, and safe
                        // since we manually trigger markForCheck() via _recomputeDerivedState()
                        const target = this.notifications.find(n => n.id === notification.id);
                        if (target) target.isRead = true;
                        this._updateUnreadCount();
                        this._recomputeDerivedState();
                    },
                    error: (err) => console.error('[NotificationPanel] MarkAsRead failed:', err)
                });
        }

        if (notification.redirectionUrl) {
            const parts = notification.redirectionUrl.split('/');
            const id = +parts.at(-1) || null;

            if (notification.redirectionUrl.includes('sales-entry-list') && id) {
                this._router.navigate([parts.slice(0, -1).join('/')], { state: { id } });
            } else {
                this._router.navigateByUrl(notification.redirectionUrl);
            }
            this.close();
        }
    }

    loadMore(event: Event): void {
        if (this.isLoading) return;
        const target = event.target as HTMLElement;
        const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
        if (isNearBottom) {
            this.searchModel.page = (this.searchModel.page ?? 1) + 1;
            this._loadNotifications(true);
        }
    }

    trackByNotificationId(index: number, notification: SignalRNotificationModel): number {
        return notification.id;
    }

    getIconClass(notification: SignalRNotificationModel): string {
        switch (notification.type) {
            case 'success': return 'fa-check';
            case 'warning': return 'fa-triangle-exclamation';
            case 'error': return 'fa-xmark';
            default: return 'fa-bell';
        }
    }

    getIconTone(notification: SignalRNotificationModel): string {
        return 'tone-info';
    }

    formattedDateTime(notification: SignalRNotificationModel): string {
        const timestamp = notification.timestamp ? new Date(notification.timestamp) : null;
        if (!timestamp || Number.isNaN(timestamp.getTime())) return '';

        return NotificationPanelComponent._dateFormatter.format(timestamp);
    }

    onSearch(value: string): void {
        this._search$.next(value);
    }

    toggleExpand(id: number, event: Event): void {
        event.stopPropagation();
        this.expandedIds.has(id) ? this.expandedIds.delete(id) : this.expandedIds.add(id);
        this._cdr.markForCheck();
    }

    isLongMessage(message: string): boolean {
        return message?.length > 100;
    }
    //#endregion
}